import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { LOGIN_ROUTE, VERIFY_SSO_LOGIN_ROUTE } from "Constants";
import { useLoginInfo } from "Hooks";
import { VerificationStatus } from "Types";
import { apiErrorToast, getBaseRoute, parseOidcStateParam } from "Utils";
import { useOidcLoginMutation } from "../services/auth.api";

type UseVerifyOIDC = {
  verificationStatus: VerificationStatus;
};

function pause(delay: number) {
  return new Promise((res) => setTimeout(res, delay));
}

const redirectUri = `${getBaseRoute()}/${LOGIN_ROUTE}/${VERIFY_SSO_LOGIN_ROUTE}`;

const useVerifyOIDC = (): UseVerifyOIDC => {
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();

  const authorizationCode = searchParams.get("code");
  const { state: ssoState, payload: navigateTo } = parseOidcStateParam(
    searchParams.get("state"),
  );

  const { hasLoaded } = useLoginInfo();

  const [oidcLogin] = useOidcLoginMutation();

  const [verificationStatus, setVerificationStatus] =
    useState<VerificationStatus>("status-unknown");

  useEffect(() => {
    const verifyOIDC = async () => {
      if (authorizationCode && ssoState) {
        try {
          await oidcLogin({
            oidcSsoLoginBody: {
              authorization_code: authorizationCode,
              redirect_uri: redirectUri,
              state: ssoState,
            },
          }).unwrap();

          setVerificationStatus("status-verified");

          if (navigateTo) {
            await pause(2000);
            navigate(navigateTo, {
              replace: true,
            });
          }
        } catch (error) {
          setVerificationStatus("status-error");
          apiErrorToast(error, "Unable to login");
        }
      } else {
        setVerificationStatus("status-error");
      }
    };

    if (hasLoaded) verifyOIDC();
  }, [authorizationCode, navigate, navigateTo, oidcLogin, hasLoaded, ssoState]);

  return {
    verificationStatus,
  };
};
export default useVerifyOIDC;
